import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { get, isUndefined } from 'lodash';
import moment from 'moment';
import { once } from 'ramda';
import { t } from '../../../../modules/infra/i18n';

import { prepareModelTimeForForm } from '../../../common/helpers/modelTime';
import { fullName, isSignedUpCustomer } from '../../../common/helpers/users';
import {
  addCustomerImage,
  changeCustomerImage,
} from '../actions';
import SimpleHeader from '../../headers/simpleHeader';
import CustomerForm from '../customerForm';
import { getCustomerById } from '../selector';
import fetchOnUpdate from '../../../common/decorators/fetchOnUpdate';
import { showNotification } from '../../../common/helpers/notifications';
import config from '../../../../config';
import { prepareCustomerForSave } from '../helpers';
import confirmModal from '../../../common/confirmModal';

class CustomerDetails extends Component {
  constructor(props) {
    super(props);

    this.isSignedUpCustomer = once(isSignedUpCustomer);
  }

  state = {
    errors: null,
    saveButtonDisabled: false,
    appointmentData: {},
  }

  componentDidMount() {
    this.props.getRewardCounter(this.props.customerId);
    this.setAppointmentData();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.customer !== nextProps.customer) {
      this.setAppointmentData(nextProps);
    }
  }

  getInitialValues() {
    const { customer } = this.props;
    if (!customer) return null;
    customer.name = fullName(customer);
    customer.avatar = get(customer, 'photos[0].url');
    customer.isBlocked = this.props.getIsBlocked(customer);
    customer.blockStatusCanBeChanged = this.props.getCanChangeCustomerBlockedStatus(customer);

    if (customer.birthDate) {
      customer.birthDate = moment(customer.birthDate, config.dateFormat).toDate();
    }

    prepareModelTimeForForm(customer, 'notifications.reminder.time');
    prepareModelTimeForForm(customer, 'notifications.barber_reminder.time');
    return customer;
  }

  setAppointmentData(props = this.props) {
    const { customer, barberUser } = props;
    const appointmentData = { customer };
    if (barberUser) {
      appointmentData.barberId = get(props, 'barberUser.id');
    }
    this.setState({ appointmentData });
  }

  saveCustomer = (model) => {
    this.setState({
      saveButtonDisabled: true,
    });
    const { customer, modifyCustomer, replaceCustomerImage, saveCustomerImage } = this.props;

    // Deletes any (not nested) key-value pair where value is an empty string or null
    // (API doesn't accept empty strings)
    for (const key in model) {
      if (model[key] === ('' || null)) {
        delete model[key];
      }
    }

    let avatar = null;
    if (model.avatar instanceof File) {
      avatar = model.avatar;
      delete model.avatar;
    }

    if (isUndefined(model.leadId)) model.leadId = null;

    return this.setCustomerBlockState(model, customer)
        .then(() => modifyCustomer(customer.id, prepareCustomerForSave(model)))
        .then(() => {
          if (!avatar) {
            return Promise.resolve();
          }
          if (customer.photos && customer.photos.length) {
            return replaceCustomerImage(customer.id, customer.photos[0], avatar);
          }
          return saveCustomerImage(customer.id, avatar);
        }).then(() => {
          this.setState({
            saveButtonDisabled: false,
            errors: null,
          });
          showNotification({
            title: t('customers:edit_details.success_notification_title'),
            level: 'success',
            message: t('customers:edit_details.success_notification_message'),
          });
        }).catch(errors => this.setState({
          errors: errors.message,
          saveButtonDisabled: false,
        }));
  }

  setCustomerBlockState(model, customer) {
    const shouldChangeBlockedState = this.props.getIsBlocked(customer) !== model.isBlocked;

    if (shouldChangeBlockedState) {
      return model.isBlocked ? this.props.blockCustomer(customer.id) : this.props.unBlockCustomer(customer.id);
    }

    return Promise.resolve();
  }

  deleteCustomer = async (e) => {
    e.preventDefault();

    const confirmed = await confirmModal({
      type: 'danger',
      title: t('customers:edit_details.delete_modal_title'),
      message: t('customers:edit_details.delete_modal_message'),
    });

    const { customerId } = this.props;
    if (confirmed) {
      this.props.deleteCustomer(customerId).then((isSuccess) => {
        if (isSuccess) {
          this.props.router.push(this.props.redirectUrl);
        }
      }).catch(err => this.setState({
        errors: err.message,
        saveButtonDisabled: false,
      }));
    }
    return false;
  }

  optOutEngagement = async (e) => {
    e.preventDefault();

    const confirmed = await confirmModal({
      type: 'danger',
      title: t('customers:edit_details.opt_out_modal_title'),
      message: t('customers:edit_details.opt_out_modal_message'),
    });

    const { modifyCustomer, customerId } = this.props;
    if (confirmed) {
      modifyCustomer(customerId, { unsubscribedCampaign: true })
        .then(() =>
        showNotification({
          title: t('customers:edit_details.success_notification_title'),
          level: 'success',
          message: t('customers:edit_details.opt_out_success_message'),
        })
      );
    }
  }

  render() {
    const { errors } = this.state;
    const { customer } = this.props;

    if (!customer) {
      return null;
    }
    const initialValues = this.getInitialValues();

    return (
      <div>
        <SimpleHeader heading={t('customers:edit_details.heading')} />
        <div className="c-wrapper">
          <div display-if={errors} className="u-margin-auto u-max-width-710">
            <div className="c-login-error-message u-mb u-mt">{errors}</div>
          </div>
          <CustomerForm
            display-if={initialValues}
            saveCustomer={this.saveCustomer}
            deleteCustomer={this.deleteCustomer}
            className="u-margin-auto u-color-white"
            initialValues={initialValues}
            disabled={this.state.saveButtonDisabled}
            owner={this.isSignedUpCustomer(customer)}
            redirectUrl={this.props.redirectUrl}
            rewardCounter={this.props.rewardCounter}
            customerId={this.props.customerId}
            appointmentData={this.state.appointmentData}
            optOutEngagement={this.optOutEngagement}
            edit
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  customer: getCustomerById(state, props.customerId),
  barberUser: state.auth.barberUser,
  params: {
    customerId: props.customerId,
    customer: getCustomerById(state, props.customerId),
  },
  actions: {
    getCustomer: props.getCustomer,
  },
  rewardCounter: state.customers.rewardCounter,
});

const mapDispatchToProps = {
  saveCustomerImage: addCustomerImage,
  replaceCustomerImage: changeCustomerImage,
};

CustomerDetails.propTypes = {
  deleteCustomer: PropTypes.func.isRequired,
  customer: PropTypes.object,
  modifyCustomer: PropTypes.func.isRequired,
  replaceCustomerImage: PropTypes.func.isRequired,
  saveCustomerImage: PropTypes.func.isRequired,
  barberUser: PropTypes.object,
  customerId: PropTypes.string.isRequired,
  getRewardCounter: PropTypes.func.isRequired,
  rewardCounter: PropTypes.object.isRequired,
  blockCustomer: PropTypes.func.isRequired,
  unBlockCustomer: PropTypes.func.isRequired,
  getIsBlocked: PropTypes.func.isRequired,
  getCanChangeCustomerBlockedStatus: PropTypes.func.isRequired,
};

const connectWithRedux = connect(
  mapStateToProps,
  mapDispatchToProps
);

const fetch = fetchOnUpdate(['customerId', 'customer'], (params, actions) => {
  if (!params.customer) {
    return actions.getCustomer(params.customerId);
  }
});

export default connectWithRedux(withRouter(fetch(CustomerDetails)));
